import { useState } from "react";
import { Building2, MapPin, Search, ShieldCheck, Tv, Users, X } from "lucide-react";
import { ClientsProvider, useClients, type Client, type ClientSettings } from "./ClientsProvider";

const formatRelativeDate = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const ClientRow = ({ client, onSelect }: { client: Client; onSelect: () => void }) => {
  return (
    <div
      onClick={onSelect}
      className="group flex cursor-pointer items-center gap-5 rounded-lg border border-zinc-800 bg-zinc-900/60 px-5 py-3.5 transition-colors hover:border-zinc-700 hover:bg-zinc-800/60"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-800">
        <Building2 size={16} className="text-zinc-400" />
      </div>
      <p className="min-w-0 flex-1 truncate text-base font-medium text-white">{client.name}</p>
      <div className="hidden shrink-0 items-center gap-8 text-sm text-zinc-500 md:flex">
        <span className="flex min-w-[90px] items-center gap-1.5 tabular-nums">
          <MapPin size={14} />
          {client.siteCount} {client.siteCount === 1 ? "site" : "sites"}
        </span>
        <span className="flex min-w-[110px] items-center gap-1.5 tabular-nums">
          <Users size={14} />
          {client.userCount}/{client.settings.userLimit} users
        </span>
        <span className="flex min-w-[120px] items-center gap-1.5 tabular-nums">
          <Tv size={14} />
          {client.deviceCount}/{client.settings.deviceLimit} devices
        </span>
        <span className="min-w-[180px] tabular-nums">Created {formatRelativeDate(client.createdAt)}</span>
      </div>
      <div className="shrink-0" title={client.settings.enabled ? "Enabled" : "Disabled"}>
        {client.settings.enabled ? (
          <span className="inline-flex items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 p-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
          </span>
        ) : (
          <span className="inline-flex items-center justify-center rounded-full border border-zinc-700 bg-zinc-800/50 p-2">
            <span className="h-2 w-2 rounded-full bg-zinc-600" />
          </span>
        )}
      </div>
    </div>
  );
};

type StatusFilter = "all" | "enabled" | "disabled";

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "enabled", label: "Enabled" },
  { key: "disabled", label: "Disabled" },
];

const ClientList = ({ clients, onSelect }: { clients: Client[]; onSelect: (client: Client) => void }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const filtered = clients.filter((client) => {
    if (statusFilter === "enabled" && !client.settings.enabled) return false;
    if (statusFilter === "disabled" && client.settings.enabled) return false;
    if (search.trim() && !client.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });

  return (
    <div className="mx-auto w-full max-w-[84rem] space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative h-9 min-w-[220px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search clients..."
            className="h-full w-full rounded-lg border border-zinc-800 bg-zinc-900 pl-9 pr-3 text-sm text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <div className="flex h-9 items-center rounded-lg border border-zinc-800 bg-zinc-900 p-0.5 text-xs">
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setStatusFilter(option.key)}
              className={`rounded-md px-3 py-1.5 transition-colors ${
                statusFilter === option.key ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-zinc-500">
          {filtered.length} {filtered.length === 1 ? "client" : "clients"}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 py-12 text-center text-sm text-zinc-500">
          No clients match this filter
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((client) => (
            <ClientRow key={client.id} client={client} onSelect={() => onSelect(client)} />
          ))}
        </div>
      )}
    </div>
  );
};

const Toggle = ({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) => (
  <button type="button" onClick={() => onChange(!value)} className="flex w-full items-center justify-between gap-4">
    <div className="text-left">
      <p className="text-sm font-medium text-white">{label}</p>
      <p className="mt-0.5 text-xs text-zinc-500">{description}</p>
    </div>
    <span
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        value ? "bg-emerald-500" : "bg-zinc-700"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
          value ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </span>
  </button>
);

const ClientEditorDialog = ({
  client,
  onSave,
  onCancel,
}: {
  client: Client;
  onSave: (data: { name: string; settings: ClientSettings }) => Promise<void>;
  onCancel: () => void;
}) => {
  const [name, setName] = useState(client.name);
  const [enabled, setEnabled] = useState(client.settings.enabled);
  const [requireTwoFactor, setRequireTwoFactor] = useState(client.settings.requireTwoFactor);
  const [userLimit, setUserLimit] = useState(client.settings.userLimit);
  const [deviceLimit, setDeviceLimit] = useState(client.settings.deviceLimit);
  const [allowCrossTenantSchedules, setAllowCrossTenantSchedules] = useState(client.settings.allowCrossTenantSchedules);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        settings: { enabled, requireTwoFactor, userLimit, deviceLimit, allowCrossTenantSchedules },
      });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onCancel}>
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-zinc-800 px-5 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Edit Client</h2>
            <button onClick={onCancel} className="rounded p-1 hover:bg-zinc-800">
              <X size={20} className="text-zinc-400" />
            </button>
          </div>
          <div className="mt-3 text-xs text-zinc-500">Created {formatDate(client.createdAt)}</div>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400">{error}</div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-zinc-400">Name</label>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Client name"
              className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-zinc-400">Settings</label>
            <div className="space-y-3">
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                <Toggle
                  label="Client enabled"
                  description="Disabled clients cannot sign in or stream to devices"
                  value={enabled}
                  onChange={setEnabled}
                />
              </div>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                <Toggle
                  label="Require two-factor authentication"
                  description="All users in this client must set up 2FA before they can sign in"
                  value={requireTwoFactor}
                  onChange={setRequireTwoFactor}
                />
              </div>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                <Toggle
                  label="Allow cross-tenant schedules"
                  description="Let this client build schedules that include media from other tenants"
                  value={allowCrossTenantSchedules}
                  onChange={setAllowCrossTenantSchedules}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-zinc-400">
                <Users size={14} />
                User limit
              </label>
              <input
                type="number"
                min={0}
                value={userLimit}
                onChange={(event) => setUserLimit(parseInt(event.target.value, 10) || 0)}
                className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-zinc-500">Currently {client.userCount} users</p>
            </div>

            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-zinc-400">
                <Tv size={14} />
                Device limit
              </label>
              <input
                type="number"
                min={0}
                value={deviceLimit}
                onChange={(event) => setDeviceLimit(parseInt(event.target.value, 10) || 0)}
                className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-zinc-500">Currently {client.deviceCount} devices</p>
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 text-xs text-zinc-500">
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-zinc-600" />
            <span>
              Settings apply to the entire client tenant and take effect immediately for new sign-ins and device
              registrations.
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-zinc-800 bg-zinc-900/50 px-5 py-4">
          <button onClick={onCancel} className="px-4 py-2 text-zinc-400 hover:text-white">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:bg-zinc-700 disabled:text-zinc-500"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ClientsContent = () => {
  const { clients, loading, error, updateClient } = useClients();
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Clients</h1>
          <p className="mt-1 text-sm text-zinc-400">Manage tenant organisations on the platform</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">{error}</div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-zinc-500">Loading...</div>
        </div>
      ) : (
        <ClientList clients={clients} onSelect={(client) => setEditingClient(client)} />
      )}

      {editingClient && (
        <ClientEditorDialog
          client={editingClient}
          onSave={async (data) => {
            await updateClient(editingClient.id, data);
            setEditingClient(null);
          }}
          onCancel={() => setEditingClient(null)}
        />
      )}
    </div>
  );
};

export default () => {
  return (
    <ClientsProvider>
      <ClientsContent />
    </ClientsProvider>
  );
};
